import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { find, reject, merge } from "lodash"
import { createdReview } from "./reviews"
import { toSearchParams } from "helpers/form"
import moment from "moment"

import { DATE_FORMAT } from "modules/datepicker/constants"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { PAGINATION_INITIAL_STATE } from "./constants"

// ACTIONS
const requested = createAction("bookings/requested")
const received = createAction("bookings/received")
const failed = createAction("bookings/failed")
const cleaned = createAction("bookings/cleaned")

const requestedPast = createAction("bookings/requestedPast")
const receivedPast = createAction("bookings/receivedPast")
const failedPast = createAction("bookings/failedPast")
const cleanedPast = createAction("bookings/cleanedPast")

const requestedExistingClientList = createAction("bookings/requestedExistingClientList")
const receivedExistingClientList = createAction("bookings/receivedExistingClientList")
const failedExistingClientList = createAction("bookings/failedExistingClientList")
export const cleanedExistingClientList = createAction("bookings/cleanedExistingClientList")
export const setClientId = createAction("bookings/setClientId")
export const cleanClientId = createAction("bookings/cleanClientId")

export const requestedIncomes = createAction("bookings/requestedIncomes")
const receivedIncomes = createAction("bookings/receivedIncomes")
const failedIncomes = createAction("bookings/failedIncomes")
const cleanedIncomes = createAction("bookings/cleanedIncomes")
export const createdIncome = createAction("bookings/createdIncome")

const requestedBooking = createAction("bookings/requestedBooking")
const receivedBooking = createAction("bookings/receivedBooking")
const failedBooking = createAction("bookings/failedBooking")
export const createdBooking = createAction("bookings/createdBooking")
export const updatedBooking = createAction("bookings/updatedBooking")
export const deletedBooking = createAction("bookings/deletedBooking")
export const cleanedBooking = createAction("bookings/cleanedBooking")

const requestedClientBookingForGuides = createAction("client/bookings/requestedClientBookingForGuides")
const receivedClientBookingForGuides = createAction("client/bookings/receivedClientBookingForGuides")
const failedClientBookingForGuides = createAction("client/bookings/failedClientBookingForGuides")

// Add action for fetching cancel reasons
export const requestedCancelReasons = createAction("bookings/requestedCancelReasons")
export const receivedCancelReasons = createAction("bookings/receivedCancelReasons")
export const failedCancelReasons = createAction("bookings/failedCancelReasons")

export const requestedClientBookingDetails = createAction("bookings/requestedClientBookingDetails")
export const receivedClientBookingDetails = createAction("bookings/receivedClientBookingDetails")
export const failedClientBookingDetails = createAction("bookings/failedClientBookingDetails")

export const requestedAdjustBookingPrice = createAction("bookings/requestedAdjustBookingPrice")
export const receivedAdjustBookingPrice = createAction("bookings/receivedAdjustBookingPrice")
export const failedAdjustBookingPrice = createAction("bookings/failedAdjustBookingPrice")

export const requestedFeeSummary = createAction("bookings/requestedFeeSummary")
export const receivedFeeSummary = createAction("bookings/receivedFeeSummary")
export const failedFeeSummary = createAction("bookings/failedFeeSummary")

export const requestedTripCustomFees = createAction("bookings/requestedTripCustomFees")
export const receivedTripCustomFees = createAction("bookings/receivedTripCustomFees")
export const failedTripCustomFees = createAction("bookings/failedTripCustomFees")

export const requestedRefundDetails = createAction("bookings/requestedRefundDetails")
export const receivedRefundDetails = createAction("bookings/receivedRefundDetails")
export const failedRefundDetails = createAction("bookings/failedRefundDetails")

export const requestedRefund = createAction("bookings/requestedRefund")
export const receivedRefund = createAction("bookings/receivedRefund")
export const failedRefund = createAction("bookings/failedRefund")

export const requestedAssetAllocation = createAction("bookings/requestedAssetAllocation")
export const receivedAssetAllocation = createAction("bookings/receivedAssetAllocation")
export const failedAssetAllocation = createAction("bookings/failedAssetAllocation")
export const cleanedAssetAllocation = createAction("bookings/cleanedAssetAllocation")

export const requestedRefundableAmount = createAction("bookings/requestedRefundableAmount")
export const receivedRefundableAmount = createAction("bookings/receivedRefundableAmount")
export const failedRefundableAmount = createAction("bookings/failedRefundableAmount")

export const requestedCancelRefundBreakdown = createAction("bookings/requestedCancelRefundBreakdown")
export const receivedCancelRefundBreakdown = createAction("bookings/receivedCancelRefundBreakdown")
export const failedCancelRefundBreakdown = createAction("bookings/failedCancelRefundBreakdown")

export const requestedCancelRefundSubmit = createAction("bookings/requestedCancelRefundSubmit")
export const receivedCancelRefundSubmit = createAction("bookings/receivedCancelRefundSubmit")
export const failedCancelRefundSubmit = createAction("bookings/failedCancelRefundSubmit")
export const cleanedCancelRefund = createAction("bookings/cleanedCancelRefund")

export const requestedTip = createAction("bookings/requestedTip")
export const receivedTip = createAction("bookings/receivedTip")
export const failedTip = createAction("bookings/failedTip")

export const requestedCardHold = createAction("bookings/requestedCardHold")
export const receivedCardHold = createAction("bookings/receivedCardHold")
export const failedCardHold = createAction("bookings/failedCardHold")

export const requestedCommunicationHistory = createAction("bookings/requestedCommunicationHistory")
export const receivedCommunicationHistory = createAction("bookings/receivedCommunicationHistory")
export const failedCommunicationHistory = createAction("bookings/failedCommunicationHistory")
export const cleanedCommunicationHistory = createAction("bookings/cleanedCommunicationHistory")

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  cancelReasons: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  booking: {
    ...LOADING_INITIAL_STATE,
    id: null,
    booked_date: "",
    booked_time: "",
    adult_count: 0,
    child_count: 0,
    cash_deposit: 0,
    booking_fee_percentage: 0,
    status: "draft",
    wizard_step: "details",
    wizard_completed: false,
    accessibility_enabled: false,
    accessibility_notes: "",
    notes: [],
    trip: {},
    client: {},
    booking_source: ""
  },
  past_trips: {
    ...LOADING_INITIAL_STATE,
    items: []
  },
  incomes: {
    ...LOADING_INITIAL_STATE,
    items: []
  },
  existing_clients: {
    ...LOADING_INITIAL_STATE,
    items: []
  },
  existing_clients_list: {
    ...LOADING_INITIAL_STATE,
    items: []
  },
  clientId: null,
  metadata: {
    ...PAGINATION_INITIAL_STATE,
    start_date: null,
    end_date: null
  },
  clientBookingForGuides: {
    ...LOADING_INITIAL_STATE,
    items: [],
    metadata: {
      ...PAGINATION_INITIAL_STATE,
      start_date: null,
      end_date: null
    }
  },
  clientBookingDetails: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  adjustBookingPrice: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  feeSummary: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  tripCustomFees: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  refundDetails: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  refund: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  assetAllocation: {
    ...LOADING_INITIAL_STATE,
    count: null
  },
  refundableAmount: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  cancelRefundBreakdown: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  cancelRefundSubmit: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  tip: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  cardHold: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  communicationHistory: {
    ...LOADING_INITIAL_STATE,
    items: [],
    metadata: null
  }
}

const bookingsReducer = createReducer(initialState, {
  [requested.type]: (state, action) => {
    setRequested(state)
    if (action.props.reset) {
      // state.items = []
      state.metadata.start_date = moment().format(DATE_FORMAT)
      state.metadata.end_date = moment().format(DATE_FORMAT)
    }
  },
  [received.type]: (state, action) => {
    setReceived(state)
    const incomingItems = Array.isArray(action.payload?.data) ? action.payload.data : []
    state.items = action.props?.append ? state.items.concat(incomingItems) : incomingItems
    Object.assign(state.metadata, action.payload.metadata)
  },
  [failed.type]: (state) => {
    setFailed(state)
  },
  [cleaned.type]: () => initialState,
  [requestedBooking.type]: (state) => {
    setRequested(state.booking)
  },
  [receivedBooking.type]: (state, action) => {
    setReceived(state.booking)
    if (action.payload.data) {
      Object.assign(state.booking, action.payload.data)
      Object.assign(state.metadata, action.payload.metadata)
    } else Object.assign(state.booking, action.payload)
  },
  [createdBooking.type]: (state, action) => {
    setReceived(state.booking)
    state.items = [action.payload, ...state.items]
    Object.assign(state.booking, action.payload)
  },
  [updatedBooking.type]: (state, action) => {
    setReceived(state.booking)
    const booking = find(state.items, ["id", action.payload.data?.id || action.payload.id]) || {}
    Object.assign(booking, action.payload.data || action.payload)
    Object.assign(state.booking, action.payload.data || action.payload)
    Object.assign(state.metadata, action.payload.metadata || {})
  },
  [createdReview.type]: (state, action) => {
    if (action.payload.reviewable_type !== "Trip") return
    if (action.payload.reviewable_id === state.booking?.trip?.id) state.booking.with_trip_review = true
    const booking = find(state.items, ["trip.id", action.payload.reviewable_id]) || {}
    booking.with_trip_review = true
  },
  [deletedBooking.type]: (state, action) => {
    setReceived(state.booking)
    state.items = reject(state.items, ["id", action.payload.id])
  },
  [failedBooking.type]: (state) => {
    setFailed(state.booking)
    state.booking.payLoading = false
  },
  [cleanedBooking.type]: (state) => {
    state.booking = initialState.booking
  },
  [requestedPast.type]: (state) => {
    setRequested(state.past_trips)
  },
  [receivedPast.type]: (state, action) => {
    setReceived(state.past_trips)
    state.past_trips.items = action.payload.data
    state.metadata = action.payload.metadata
  },
  [failedPast.type]: (state) => {
    setFailed(state.past_trips)
  },
  [cleanedPast.type]: (state) => {
    setFailed(state.past_trips)
    state.past_trips = initialState.past_trips
  },
  [requestedClientBookingForGuides.type]: (state) => {
    setRequested(state.clientBookingForGuides)
  },
  [receivedClientBookingForGuides.type]: (state, { payload, props }) => {
    setReceived(state.clientBookingForGuides)
    if (props?.isAppend && Array.isArray(payload?.data)) {
      state.clientBookingForGuides.items = [
        ...(Array.isArray(state.clientBookingForGuides.items) ? state.clientBookingForGuides.items : []),
        ...payload.data
      ]
    } else {
      state.clientBookingForGuides.items = Array.isArray(payload?.data) ? payload.data : []
    }
    state.clientBookingForGuides.metadata = payload?.metadata
  },
  [failedClientBookingForGuides.type]: (state) => {
    setFailed(state.clientBookingForGuides)
  },
  [requestedClientBookingDetails.type]: (state, action) => {
    // Keep existing data while re-fetching the SAME booking (avoids flicker), but clear it when
    // switching to a DIFFERENT booking — otherwise the previous booking's data lingers during
    // load and consumers (e.g. the details header's Edit button) act on the wrong uuid.
    const requestedUuid = action?.props?.uuid
    const currentUuid = state.clientBookingDetails.data?.uuid
    if (!state.clientBookingDetails.firstLoaded || (requestedUuid && requestedUuid !== currentUuid)) {
      state.clientBookingDetails.data = null
    }
    setRequested(state.clientBookingDetails)
  },
  [receivedClientBookingDetails.type]: (state, action) => {
    setReceived(state.clientBookingDetails)
    state.clientBookingDetails.data = action.payload
  },
  [failedClientBookingDetails.type]: (state) => {
    state.clientBookingDetails.data = null
    setFailed(state.clientBookingDetails)
  },
  [requestedIncomes.type]: (state) => {
    setRequested(state.incomes)
  },
  [receivedIncomes.type]: (state, action) => {
    setReceived(state.incomes)
    state.incomes.items = action.payload
  },
  [failedIncomes.type]: (state) => {
    setFailed(state.incomes)
  },
  [cleanedIncomes.type]: (state) => {
    state.incomes = initialState.incomes
  },
  [createdIncome.type]: (state, action) => {
    setReceived(state.incomes)
    if (action.payload?.incomes?.length) state.incomes.items = action.payload.incomes
    const booking = find(state.items, ["id", action.payload?.booking?.id]) || {}
    if (booking) {
      merge(booking, action.payload?.booking)
      merge(state.booking, action.payload?.booking)
    }
  },
  [requestedExistingClientList.type]: (state) => {
    setRequested(state.existing_clients_list)
  },
  [receivedExistingClientList.type]: (state, action) => {
    setReceived(state.existing_clients_list)
    state.existing_clients_list.items = action.payload
  },
  [failedExistingClientList.type]: (state) => {
    setFailed(state.existing_clients_list)
  },
  [cleanedExistingClientList.type]: (state) => {
    setFailed(state.existing_clients_list)
    state.existing_clients_list = initialState.existing_clients_list
    state.clientId = null
  },
  [setClientId.type]: (state, action) => {
    state.clientId = action.payload // Set the clientId in the Redux store
  },

  [cleanClientId.type]: (state) => {
    state.clientId = null // Reset the clientId to null
  },
  [requestedCancelReasons.type]: (state) => {
    state.cancelReasons.data = []
    setRequested(state.cancelReasons)
  },
  [receivedCancelReasons.type]: (state, action) => {
    state.cancelReasons.data = action.payload.data || action.payload || []
    setReceived(state.cancelReasons)
  },
  [failedCancelReasons.type]: (state) => {
    state.cancelReasons.data = []
    setFailed(state.cancelReasons)
  },
  [requestedAdjustBookingPrice.type]: (state) => {
    setRequested(state.adjustBookingPrice)
  },
  [receivedAdjustBookingPrice.type]: (state, payload) => {
    setReceived(state.adjustBookingPrice)
    state.adjustBookingPrice.data = payload
  },
  [failedAdjustBookingPrice.type]: (state) => {
    setFailed(state.adjustBookingPrice)
  },
  [requestedFeeSummary.type]: (state) => {
    setRequested(state.feeSummary)
  },
  [receivedFeeSummary.type]: (state, { payload }) => {
    setReceived(state.feeSummary)
    state.feeSummary.data = payload?.message ?? payload
  },
  [failedFeeSummary.type]: (state) => {
    setFailed(state.feeSummary)
  },
  [requestedTripCustomFees.type]: (state) => {
    setRequested(state.tripCustomFees)
  },
  [receivedTripCustomFees.type]: (state, { payload }) => {
    setReceived(state.tripCustomFees)
    state.tripCustomFees.data = payload?.message ?? payload
  },
  [failedTripCustomFees.type]: (state) => {
    setFailed(state.tripCustomFees)
  },
  [requestedRefundDetails.type]: (state) => {
    state.refundDetails.data = null
    setRequested(state.refundDetails)
  },
  [receivedRefundDetails.type]: (state, { payload }) => {
    setReceived(state.refundDetails)
    state.refundDetails.data = payload
  },
  [failedRefundDetails.type]: (state) => {
    setFailed(state.refundDetails)
  },
  [requestedRefund.type]: (state) => {
    state.refund.data = null
    setRequested(state.refund)
  },
  [receivedRefund.type]: (state, { payload }) => {
    setReceived(state.refund)
    state.refund.data = payload
  },
  [failedRefund.type]: (state) => {
    setFailed(state.refund)
  },
  [requestedAssetAllocation.type]: (state) => {
    setRequested(state.assetAllocation)
  },
  [receivedAssetAllocation.type]: (state, { payload }) => {
    setReceived(state.assetAllocation)
    state.assetAllocation.count = payload?.assets_needed || null
  },
  [failedAssetAllocation.type]: (state) => {
    setFailed(state.assetAllocation)
    state.assetAllocation = initialState.assetAllocation
  },
  [cleanedAssetAllocation.type]: (state) => {
    state.assetAllocation = initialState.assetAllocation
  },
  [requestedRefundableAmount.type]: (state) => {
    state.refundableAmount.data = null
    setRequested(state.refundableAmount)
  },
  [receivedRefundableAmount.type]: (state, { payload }) => {
    setReceived(state.refundableAmount)
    state.refundableAmount.data = payload
  },
  [failedRefundableAmount.type]: (state) => {
    setFailed(state.refundableAmount)
  },
  [requestedCancelRefundBreakdown.type]: (state) => {
    state.cancelRefundBreakdown.data = null
    setRequested(state.cancelRefundBreakdown)
  },
  [receivedCancelRefundBreakdown.type]: (state, { payload }) => {
    setReceived(state.cancelRefundBreakdown)
    state.cancelRefundBreakdown.data = payload
  },
  [failedCancelRefundBreakdown.type]: (state) => {
    setFailed(state.cancelRefundBreakdown)
  },
  [requestedCancelRefundSubmit.type]: (state) => {
    state.cancelRefundSubmit.data = null
    setRequested(state.cancelRefundSubmit)
  },
  [receivedCancelRefundSubmit.type]: (state, { payload }) => {
    setReceived(state.cancelRefundSubmit)
    state.cancelRefundSubmit.data = payload
  },
  [failedCancelRefundSubmit.type]: (state) => {
    setFailed(state.cancelRefundSubmit)
  },
  [cleanedCancelRefund.type]: (state) => {
    state.cancelRefundBreakdown = initialState.cancelRefundBreakdown
    state.cancelRefundSubmit = initialState.cancelRefundSubmit
    state.cancelReasons = initialState.cancelReasons
  },
  [requestedTip.type]: (state) => {
    setRequested(state.tip)
  },
  [receivedTip.type]: (state, action) => {
    setReceived(state.tip)
    state.tip.data = action.payload
  },
  [failedTip.type]: (state) => {
    setFailed(state.tip)
  },
  [requestedCardHold.type]: (state) => {
    setRequested(state.cardHold)
  },
  [receivedCardHold.type]: (state, action) => {
    setReceived(state.cardHold)
    state.cardHold.data = action.payload
  },
  [failedCardHold.type]: (state) => {
    setFailed(state.cardHold)
  },
  [requestedCommunicationHistory.type]: (state) => {
    setRequested(state.communicationHistory)
  },
  [receivedCommunicationHistory.type]: (state, action) => {
    setReceived(state.communicationHistory)
    const payload = action.payload || {}
    const incoming = Array.isArray(payload.data) ? payload.data : []
    state.communicationHistory.items = action.props?.append ? state.communicationHistory.items.concat(incoming) : incoming
    state.communicationHistory.metadata = payload.metadata || null
  },
  [failedCommunicationHistory.type]: (state) => {
    setFailed(state.communicationHistory)
  },
  [cleanedCommunicationHistory.type]: (state) => {
    state.communicationHistory = initialState.communicationHistory
  }
})
export default bookingsReducer

const getItems = (url, nextPage, params, dispatch, getState, isAppend = false) => {
  const serializedParams = toSearchParams({ ...params, include_unavailable_dates: true }, null)

  return dispatch(
    api({
      url,
      onStart: requested.type,
      onSuccess: received.type,
      onError: failed.type,
      params: serializedParams,
      props: { reset: !nextPage, append: Boolean(isAppend) }
    })
  )
}

const getItem = (url, id, dispatch, getState, noCache = false, isBookAgain = false, include_boats = false) => {
  const sameIdOrUuid = (item) => +item.id === +id || item.uuid === id
  const state = getState()
  const bookingsBooking = find(state.bookings.items, sameIdOrUuid)
  const dashboardBooking = find(state.dashboard.bookings, sameIdOrUuid)
  const booking = bookingsBooking || dashboardBooking

  if (booking && !noCache) {
    dispatch(receivedBooking(booking))
    return new Promise((resolve) => resolve(booking))
  }
  return dispatch(
    api({
      url,
      onStart: requestedBooking.type,
      onSuccess: receivedBooking.type,
      onError: failedBooking.type,
      params: {
        include_unavailable_dates: true,
        include_stripe_cards: true,
        is_book_again: Boolean(isBookAgain),
        include_boats: Boolean(include_boats)
      }
    })
  )
}

// PUBLIC ACTIONS

export const getGuideBookings = (params, nextPage) => (dispatch, getState) =>
  getItems("/guide/bookings", nextPage, params, dispatch, getState)

export const getGuideBookingsV2 =
  (params, nextPage, isAppend = false) =>
  (dispatch, getState) =>
    getItems("/guide/bookings/get_booking", nextPage, params, dispatch, getState, isAppend)

export const getBookings = (nextPage, params) => (dispatch, getState) =>
  getItems("/client/trip_bookings", nextPage, params, dispatch, getState)

export const getExistingClientDetail = (param) =>
  api({
    url: `/guide/clients/search`,
    params: param,
    onStart: requestedExistingClientList.type,
    onSuccess: receivedExistingClientList.type,
    onError: failedExistingClientList.type
  })

export const getExistingClientListing = () =>
  api({
    url: `/guide/clients/list`,
    onStart: requestedExistingClientList.type,
    onSuccess: receivedExistingClientList.type,
    onError: failedExistingClientList.type
  })

export const getGuidePastBookingsForClient = (params = {}) =>
  api({
    url: `/guide/bookings/past`,
    params,
    onStart: requestedPast.type,
    onSuccess: receivedPast.type,
    onError: failedPast.type
  })

export const getGuideBooking = (id) => (dispatch, getState) => getItem(`/guide/bookings/${id}`, id, dispatch, getState)
export const getBooking = (uuid, noCache, isBookAgain, include_boats) => (dispatch, getState) =>
  getItem(`/trip_bookings/${uuid}`, uuid, dispatch, getState, noCache, isBookAgain, include_boats)

export const getPublicBooking = (uuid) => (dispatch) =>
  dispatch(
    api({
      url: `/trip_bookings/${uuid}`,
      noAuth: true,
      onStart: requestedBooking.type,
      onSuccess: receivedBooking.type,
      onError: failedBooking.type,
      params: { include_unavailable_dates: true, include_boats: true }
    })
  )

export const getCancelReasons = (params) => (dispatch) =>
  dispatch(
    api({
      url: "/guide/bookings/reasons",
      onStart: requestedCancelReasons.type,
      onSuccess: receivedCancelReasons.type,
      onError: failedCancelReasons.type,
      method: "get",
      params
    })
  )

export const createBooking = (tripId, data) =>
  api({
    url: `/trips/${tripId}/trip_bookings`,
    data,
    method: "post",
    onStart: requestedBooking.type,
    onSuccess: createdBooking.type,
    onError: failedBooking.type,
    props: { showDetailedError: true },
    params: { include_unavailable_dates: true, include_stripe_cards: true }
  })

export const createGuideBooking = (data) =>
  api({
    url: `/guide/bookings`,
    data,
    method: "post",
    onStart: requestedBooking.type,
    onSuccess: createdBooking.type,
    onError: failedBooking.type
  })

export const updateBooking = (uuid, data) =>
  api({
    url: `/trip_bookings/${uuid}`,
    method: "put",
    data,
    onStart: requestedBooking.type,
    onSuccess: updatedBooking.type,
    onError: failedBooking.type,
    props: { showDetailedError: true }
  })

export const bookAgainGuideBooking = (booking_id, data) =>
  api({
    url: `/guide/bookings/${booking_id}/rebook`,
    method: "post",
    data,
    onStart: requestedBooking.type,
    onSuccess: createdBooking.type,
    onError: failedBooking.type
  })

export const updateGuideBooking = (id, data, paymentMethodId) =>
  api({
    url: `/guide/bookings/${id}`,
    method: "put",
    data,
    onStart: requestedBooking.type,
    onSuccess: updatedBooking.type,
    onError: failedBooking.type
  })

export const createClientBookingIncomeRecord = (booking_id) =>
  api({
    url: `/trip_bookings/${booking_id}/payments`,
    method: "post",
    onStart: requestedIncomes.type,
    onSuccess: createdIncome.type,
    onError: failedIncomes.type,
    props: { showDetailedError: true }
  })

export const captureClientBookingIncomeRecord = (booking_id, record_id) =>
  api({
    url: `/trip_bookings/${booking_id}/payments/${record_id}/capture`,
    method: "put",
    onStart: requestedIncomes.type,
    onSuccess: createdIncome.type,
    onError: failedIncomes.type,
    props: { showDetailedError: true }
  })

export const getGuideBookingIncomes = (booking_id) =>
  api({
    url: `/guide/bookings/${booking_id}/payments`,
    onStart: requestedIncomes.type,
    onSuccess: receivedIncomes.type,
    onError: failedIncomes.type,
    props: { showDetailedError: true }
  })

export const getClientBookingIncomes = (booking_id) =>
  api({
    url: `/trip_bookings/${booking_id}/payments`,
    onStart: requestedIncomes.type,
    onSuccess: receivedIncomes.type,
    onError: failedIncomes.type,
    props: { showDetailedError: true }
  })

export const createGuideBookingIncomeRecord = (booking_id, data) =>
  api({
    url: `/guide/bookings/${booking_id}/payments/record`,
    method: "post",
    data,
    onStart: requestedIncomes.type,
    onSuccess: createdIncome.type,
    onError: failedIncomes.type,
    props: { showDetailedError: true }
  })

export const captureGuideBookingIncomeRecord = (booking_id, record_id) =>
  api({
    url: `/guide/bookings/${booking_id}/payments/${record_id}/capture`,
    method: "put",
    onStart: requestedIncomes.type,
    onSuccess: createdIncome.type,
    onError: failedIncomes.type,
    props: { showDetailedError: true }
  })

export const createCardHold = (booking_id, data) =>
  api({
    url: `/guide/bookings/${booking_id}/authorizations`,
    method: "post",
    data,
    onStart: requestedCardHold.type,
    onSuccess: receivedCardHold.type,
    onError: failedCardHold.type,
    props: { showDetailedError: true }
  })

export const captureCardHold = (booking_id, income_id, data) =>
  api({
    url: `/guide/bookings/${booking_id}/payments/${income_id}/capture`,
    method: "put",
    data,
    onStart: requestedCardHold.type,
    onSuccess: receivedCardHold.type,
    onError: failedCardHold.type,
    props: { showDetailedError: true }
  })

export const releaseCardHold = (booking_id, income_id) =>
  api({
    url: `/guide/bookings/${booking_id}/payments/${income_id}/release`,
    method: "post",
    onStart: requestedCardHold.type,
    onSuccess: receivedCardHold.type,
    onError: failedCardHold.type,
    props: { showDetailedError: true }
  })

export const captureGuideBookingTipRecord = (booking_id, record_id) =>
  api({
    url: `/guide/bookings/${booking_id}/tips/${record_id}/capture`,
    method: "put",
    onStart: requestedTip.type,
    onSuccess: receivedTip.type,
    onError: failedTip.type,
    props: { showDetailedError: true }
  })

export const takeGuideBookingPayment = (booking_id, data) =>
  api({
    url: `/guide/bookings/${booking_id}/payments/record`,
    method: "post",
    data,
    onStart: requestedIncomes.type,
    onSuccess: createdIncome.type,
    onError: failedIncomes.type,
    props: { showDetailedError: true }
  })

export const createGuideBookingIncomeRefund = (booking_id, record_id, data) =>
  api({
    url: `/guide/bookings/${booking_id}/payments/${record_id}/refund`,
    method: "post",
    data,
    onStart: requestedIncomes.type,
    onSuccess: createdIncome.type,
    onError: failedIncomes.type,
    props: { showDetailedError: true }
  })
export const saveGuideBooking = (id, data, isBookAgain) => {
  if (isBookAgain || !id) {
    return createGuideBooking(data)
  }
  return updateGuideBooking(id, data)
}

export const saveBooking = (id, data) => (id ? updateBooking(id, data) : createBooking(data))
export const cancelGuideBooking = (id) =>
  api({
    url: `/guide/bookings/${id}`,
    method: "put",
    data: { status: "canceled" },
    onStart: requestedBooking.type,
    onSuccess: updatedBooking.type,
    onError: failedBooking.type
  })

export const cancelClientBooking = (uuid) =>
  api({
    url: `/trip_bookings/${uuid}/update_status`,
    method: "put",
    data: { status: "canceled" },
    onStart: requestedBooking.type,
    onSuccess: updatedBooking.type,
    onError: failedBooking.type
  })

export const confirmClientBooking = (uuid) =>
  api({
    url: `/trip_bookings/${uuid}/update_status`,
    method: "put",
    data: { status: "booked" },
    onStart: requestedBooking.type,
    onSuccess: updatedBooking.type,
    onError: failedBooking.type
  })

export const deleteClientBooking = (uuid) =>
  api({
    url: `/trip_bookings/${uuid}`,
    method: "delete",
    onStart: requestedBooking.type,
    onSuccess: deletedBooking.type,
    onError: failedBooking.type
  })

export const deleteClientDraftBooking = (uuid) =>
  api({
    url: `/trip_bookings/${uuid}/destroy_draft`,
    method: "delete",
    onStart: requestedBooking.type,
    onSuccess: deletedBooking.type,
    onError: failedBooking.type
  })

export const getClientBookingsForGuides = (id, isAppend = false, params = {}) =>
  api({
    url: `/guide/clients/${id}/client_bookings`,
    params,
    onStart: requestedClientBookingForGuides.type,
    onSuccess: receivedClientBookingForGuides.type,
    onError: failedClientBookingForGuides.type,
    props: { isAppend }
  })

export const getClientBookingDetails = (uuid) =>
  api({
    url: `/trip_bookings/${uuid}/booking_detail`,
    onStart: requestedClientBookingDetails.type,
    onSuccess: receivedClientBookingDetails.type,
    onError: failedClientBookingDetails.type,
    props: { uuid }
  })

// Re-fetches /booking_detail after a mutation. That endpoint can briefly return the pre-update
// snapshot (read-after-write lag), so a single immediate re-fetch may land stale and leave the
// details view showing old data until a manual refresh. We capture the snapshot already in the
// store (the pre-edit data) and retry until the response differs from it, or attempts run out.
// Degrades to a single fetch when the endpoint is already consistent.
export const refreshClientBookingDetails =
  (uuid, { retries = 3, delay = 700 } = {}) =>
  async (dispatch, getState) => {
    const snapshot = JSON.stringify(getState().bookings.clientBookingDetails.data ?? null)
    let result = null
    for (let attempt = 0; attempt <= retries; attempt++) {
      result = await dispatch(getClientBookingDetails(uuid))
      if (attempt === retries || JSON.stringify(result ?? null) !== snapshot) return result
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
    return result
  }

export const cleanBookings = () => (dispatch) => dispatch(cleaned())
export const cleanBooking = () => (dispatch) => dispatch(cleanedBooking())
export const cleanBookingIncomes = () => (dispatch) => dispatch(cleanedIncomes())
export const cleanPastBooking = () => (dispatch) => dispatch(cleanedPast())

// Add bulk cancel API for guide bookings
export const cancelGuideBookingsBulk = (bookings) =>
  api({
    url: "/guide/bookings/cancel_booking",
    method: "put",
    data: { bookings },
    onStart: requestedBooking.type,
    onSuccess: updatedBooking.type,
    onError: failedBooking.type
  })

export const guideBookingStatus = (uuid, status) =>
  api({
    url: `/trip_bookings/${uuid}/status_action?status=${status}`,
    method: "patch",
    onStart: requestedBooking.type,
    onSuccess: updatedBooking.type,
    onError: failedBooking.type
  })

export const adjustBookingPrice = (id, data) =>
  api({
    url: `guide/bookings/${id}/adjust_price`,
    method: "put",
    onStart: requestedAdjustBookingPrice.type,
    onSuccess: receivedAdjustBookingPrice.type,
    onError: failedAdjustBookingPrice.type,
    data
  })

export const getFeeSummary = () =>
  api({
    url: "guide/settings/fee_summary",
    method: "get",
    onStart: requestedFeeSummary.type,
    onSuccess: receivedFeeSummary.type,
    onError: failedFeeSummary.type
  })

export const getTripCustomFees = (tripUUID) =>
  api({
    url: `guide/trips/${tripUUID}/custom_fees`,
    method: "get",
    onStart: requestedTripCustomFees.type,
    onSuccess: receivedTripCustomFees.type,
    onError: failedTripCustomFees.type
  })

export const getRefundDetails = (uuid) =>
  api({
    url: `/trip_bookings/${uuid}/refund_detail`,
    onStart: requestedRefundDetails.type,
    onSuccess: receivedRefundDetails.type,
    onError: failedRefundDetails.type
  })

export const createRefund = (uuid, data) =>
  api({
    url: `/trip_bookings/${uuid}/refund`,
    method: "post",
    onStart: requestedRefund.type,
    onSuccess: receivedRefund.type,
    onError: failedRefund.type,
    data
  })

export const getAssetAllocation = (params) =>
  api({
    url: "/guide/bookings/calculate_assets",
    method: "get",
    params: params,
    onStart: requestedAssetAllocation.type,
    onSuccess: receivedAssetAllocation.type,
    onError: failedAssetAllocation.type
  })

export const cleanAssetAllocation = () => (dispatch) => dispatch(cleanedAssetAllocation())

// Thunk action to fetch refundable amount
export const fetchRefundableAmount = (bookingId, reasonId) => async (dispatch, getState) => {
  try {
    dispatch(requestedRefundableAmount())

    const response = await api({
      url: `/trip_bookings/${bookingId}/refundable_amount`,
      method: "GET",
      params: { reason_id: reasonId },
      onStart: requestedRefundableAmount.type,
      onSuccess: receivedRefundableAmount.type,
      onError: failedRefundableAmount.type
    })(dispatch, getState)

    return response
  } catch (error) {
    dispatch(failedRefundableAmount())
    throw error
  }
}

export const fetchCancelRefundBreakdown = (bookingId) =>
  api({
    url: `/trip_bookings/${bookingId}/payment_breakdown`,
    method: "get",
    onStart: requestedCancelRefundBreakdown.type,
    onSuccess: receivedCancelRefundBreakdown.type,
    onError: failedCancelRefundBreakdown.type
  })

export const submitCancelAndRefund = (bookingId, data) =>
  api({
    url: `guide/bookings/${bookingId}/cancel_and_refund_booking`,
    method: "put",
    onStart: requestedCancelRefundSubmit.type,
    onSuccess: receivedCancelRefundSubmit.type,
    onError: failedCancelRefundSubmit.type,
    data
  })

export const cleanCancelRefund = () => (dispatch) => dispatch(cleanedCancelRefund())

export const COMMUNICATION_HISTORY_PAGE_SIZE = 3

export const getCommunicationHistory = (bookingId, params = {}) => {
  const page = params.page ?? 1
  return api({
    url: `/guide/bookings/${bookingId}/communication_history`,
    params: { page, per_page: COMMUNICATION_HISTORY_PAGE_SIZE, ...params },
    onStart: requestedCommunicationHistory.type,
    onSuccess: receivedCommunicationHistory.type,
    onError: failedCommunicationHistory.type,
    props: { append: page > 1 }
  })
}

export const cleanCommunicationHistory = () => (dispatch) => dispatch(cleanedCommunicationHistory())

export const getAutoCollectWindowStatus = (id) =>
  api({
    url: `guide/bookings/${id}/auto_collect_window_status`,
    method: "get"
  })

export const patchBookingPaymentOption = (uuid, paymentOption) =>
  api({
    url: `guide/bookings/${uuid}/update_payment_option`,
    method: "patch",
    data: { trip_booking: { payment_option: paymentOption } }
  })
